import React, { useState, useMemo, useEffect } from 'react';
import Globalize from '../../actions/languages/Globalize';
import { getVersionCounts } from '../../constants/menuMapping';
import Loader from '../dashboard/Loader/Loader';
import MenuListing from './MenuListing';
import ErrorMessage from './ErrorMessage';
import { useFeatures } from '../../contexts/FeaturesContext';
import './MenuSearchAndFilter.css';

const MenuSearchAndFilter = ({ from = 'greetingDashboard', ...props }) => {
  const { features, isLoading, error, retryFeatures, isRetrying, user } = useFeatures();
  const [searchTerm, setSearchTerm] = useState('');
  const [versionFilter, setVersionFilter] = useState('all');
  const [filteredFeatures, setFilteredFeatures] = useState([]);
  // Update filtered features when features change
  useEffect(() => {
    setFilteredFeatures(features);
  }, [features]);

  // Get version counts for filter buttons
  const versionCounts = useMemo(() => getVersionCounts(features), [features]);

  // Tab configuration
  const tabs = [
    {
      key: 'all',
      label: 'All',
      color: '#007bff',
      count: versionCounts.all
    },
    {
      key: 'v1',
      label: 'V1',
      color: '#28a745',
      count: versionCounts.v1
    },
    {
      key: 'v3',
      label: 'V3',
      color: '#dc3545',
      count: versionCounts.v3
    }
  ];

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setFilteredFeatures(
      features.filter((item) => {
        return Globalize.getString(item.feature_name)
          .toLowerCase()
          .includes(e.target.value.toLowerCase());
      })
    );
  };

  const handleVersionFilter = (version) => {
    setVersionFilter(version);
    if (version === 'all') {
      setFilteredFeatures(features);
    } else if (version === 'v1') {
      setFilteredFeatures(
        features.filter((item) => {
          return !item.route?.includes('/v3/');
        })
      );
    } else if (version === 'v3') {
      setFilteredFeatures(
        features.filter((item) => {
          return item.route?.includes('/v3/');
        })
      );
    }
  };

  if (isLoading) {
    return <Loader />;
  }
  if (error) {
    return (
      <ErrorMessage
        title="Failed to Load Menu Items"
        message="We couldn't load the menu items. This might be due to a network issue or server problem."
        onRetry={retryFeatures}
        isRetrying={isRetrying}
        showRetryButton={true}
      />
    );
  }

  return (
    <div className="menu-search-filter-container">
      {from !== 'dashboardNavigation' && versionCounts?.all > 10 && (
        <div className="search-filter-controls">
          {/* Header with tabs on left and search on right */}
          <div className="search-filter-header">

            {/* Search input on the right */}
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              <div className="search-icon">
                🔍
              </div>
            </div>

            {/* Tabs on the left */}

            <div className="version-tabs">
              {tabs.filter(tab => tab.count > 0)?.map((tab) => (
                <button
                  key={tab.key}
                  className={`version-tab ${versionFilter === tab.key ? 'active' : ''}`}
                  onClick={() => handleVersionFilter(tab.key)}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Results Summary - Now on its own line */}
      {from !== 'dashboardNavigation' && versionCounts?.all > 10 && (
        <div className="results-summary">
          Showing {filteredFeatures?.length} of {versionCounts?.all} menu items
          {searchTerm && ` matching "${searchTerm}"`}
          {versionFilter !== 'all' && ` in ${versionFilter.toUpperCase()}`}
        </div>
      )}
      {props?.isFeaturesLoading ? <Loader /> : (
        <MenuListing filteredFeatures={filteredFeatures} from={from} user={user} {...props}  />
      )}
    </div>
  );
};

export default MenuSearchAndFilter;
